/**
 * Hook do tenant atual. Lê o documento `tenants/{tid}` e expõe subscription + features.
 */
import { useQuery } from '@tanstack/react-query'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuth } from '@/hooks/useAuth'
import type { Tenant } from '@/types'
import { tenantHasFeature } from '@/constants/plans'

export function useTenant() {
  const { user } = useAuth()
  const tenantId = user?.tenantId || null

  const query = useQuery({
    queryKey: ['tenant', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<Tenant | null> => {
      if (!tenantId) return null
      const snap = await getDoc(doc(db, 'tenants', tenantId))
      if (!snap.exists()) return null
      return { id: snap.id, ...(snap.data() as Omit<Tenant, 'id'>) }
    },
  })

  const tenant = query.data || null
  const features = tenant?.subscription.features || []
  const addons = tenant?.subscription.addons || []

  const hasFeature = (feature: string): boolean => {
    if (!tenant) return false
    return tenantHasFeature(tenant.subscription.plan_id, [...features, ...addons], feature)
  }

  const isTrialing = tenant?.subscription.status === 'trial'
  const isActive = ['trial', 'active'].includes(tenant?.subscription.status || '')
  const isSuspended = tenant?.subscription.status === 'suspended'

  return {
    tenant,
    loading: query.isLoading,
    error: query.error,
    hasFeature,
    isTrialing,
    isActive,
    isSuspended,
    refresh: query.refetch,
  }
}
